import React from "react";
import {
  ArrowRight,
  BellRinging,
  CalendarBlank,
  Car,
  ChartBar,
  CheckCircle,
  ForkKnife,
  Gift,
  GraduationCap,
  Hospital,
  HouseLine,
  MapPin,
  Megaphone,
  Package,
  PhoneCall,
  ShoppingBagOpen,
  Storefront,
  Target,
  UploadSimple,
} from "@phosphor-icons/react";
import RghtnowMarketingHeader from "./RghtnowMarketingHeader";
import RghtnowMarketingFooter from "./RghtnowMarketingFooter";
import AnimatedStats from "../components/AnimatedStats";
import "./rghtnow-pages.css";

const upload = (path) => `https://rghtnow.com/wp-content/uploads/${path}`;

const whyAdvertise = [
  {
    image: upload("2026/05/mission.svg"),
    title: "Growing User Base",
    text: "Tap into a rapidly expanding community of active shoppers who use RGHT NOW daily for their essential needs.",
  },
  {
    image: upload("2026/06/High-Performance.svg"),
    title: "High Purchase Intent Audience",
    text: "Advertise to users who are already in buying mode - not just browsing, but actively looking to purchase.",
  },
  {
    image: upload("2026/06/Mobile-First.svg"),
    title: "Mobile-First Platform",
    text: "Reach customers where they are. Our platform is optimized for mobile, ensuring maximum ad visibility.",
  },
  {
    image: upload("2026/05/mission.svg"),
    title: "Category-Specific Targeting",
    text: "Place your ads within relevant categories - grocery, food, pharmacy, and more - for pinpoint precision.",
  },
  {
    image: upload("2026/05/location-1.svg"),
    title: "Dubai-Focused Reach",
    text: "Connect with customers in Dubai and the UAE where purchasing power is high and demand is growing fast.",
  },
  {
    image: upload("2026/05/Cost-Effective.svg"),
    title: "Measurable Performance",
    text: "Track impressions, clicks, and conversions in real time. Every dirham you spend is fully accountable.",
  },
];

const advertisingSolutions = [
  {
    image: upload("2026/06/Home-Banner.svg"),
    title: "Homepage Banner Ads",
    text: "Premium visibility on the app homepage - the first thing every user sees when they open RGHT NOW.",
  },
  {
    image: upload("2026/06/Category.svg"),
    title: "Category Sponsorships",
    text: "Own a category. Promote your brand within specific verticals like grocery, pharmacy, or food.",
  },
  {
    image: upload("2026/06/Product-Listing.svg"),
    title: "Featured Product Listings",
    text: "Highlight your products at the top of search results and category pages to capture buying intent.",
  },
  {
    image: upload("2026/06/Notification.svg"),
    title: "Push Notifications",
    text: "Reach customers directly on their phones with timely, personalized promotional messages.",
  },
  {
    image: upload("2026/06/Promotions.svg"),
    title: "In-App Promotions",
    text: "Drive engagement and sales with interactive in-app banners, pop-ups, and promotional tiles.",
  },
  {
    image: upload("2026/06/Campaigns.svg"),
    title: "Seasonal Campaigns",
    text: "Ramadan, Eid, Back-to-School, National Day - leverage high-traffic seasons for maximum impact.",
  },
];

const advertiserTypes = [
  { icon: Storefront, title: "Supermarkets & Grocery Stores" },
  { icon: ForkKnife, title: "Restaurants & Food Brands" },
  { icon: Hospital, title: "Pharmacies & Healthcare" },
  { icon: Package, title: "Electronics & Mobile Brands" },
  { icon: Gift, title: "Flower & Gift Shops" },
  { icon: ChartBar, title: "Financial Services" },
  { icon: Car, title: "Automotive Businesses" },
  { icon: HouseLine, title: "Hospitality Brands" },
  { icon: GraduationCap, title: "Educational Institutions" },
];

const performanceBenefits = [
  "Increased Brand Awareness across the platform",
  "Higher Customer Engagement with your products",
  "Increased App Visibility and discoverability",
  "Targeted Audience Reach by category and location",
  "Measurable Results with real-time reporting",
  "Improved Sales Performance and conversion rates",
];

const marketplaceSegments = [
  { image: upload("2026/05/grocery-img-300x300.png"), title: "Grocery" },
  { image: upload("2026/05/food-300x300.png"), title: "Food Ordering & Delivery" },
  { image: upload("2026/05/taxi-300x300.png"), title: "Taxi Booking Services" },
  { image: upload("2026/05/pharmacy-300x300.png"), title: "Pharmacy & Healthcare" },
  { image: upload("2026/05/electronic-300x300.png"), title: "Electronics" },
  { image: upload("2026/05/Handy-Man-300x300.png"), title: "Handyman Services" },
  { image: upload("2026/05/flower-shop-300x300.png"), title: "Flowers & Gifts" },
  { image: upload("2026/05/Uniform-300x300.png"), title: "Fashion & Uniforms" },
  { image: upload("2026/05/Mobile-Recharge-300x300.png"), title: "Mobile Recharge & Utility Payments" },
  { image: upload("2026/05/Local-Shop-300x300.png"), title: "Local Store Marketplace" },
];

const formSections = [
  {
    title: "Business Information",
    fields: [
      { label: "Business / Brand Name", type: "text" },
      { label: "Industry Type", type: "select", options: ["Grocery", "Food", "Beauty", "Pharmacy", "Transport", "Services", "Pet Shop", "Organic", "Other"] },
      { label: "Website / Social Media Link", type: "url" },
      { label: "Business Address", type: "textarea", wide: true },
      { label: "City", type: "text" },
      { label: "State", type: "text" },
    ],
  },
  {
    title: "Contact Information",
    fields: [
      { label: "Contact Person Name", type: "text" },
      { label: "Designation", type: "text" },
      { label: "Email Address", type: "email" },
      { label: "Mobile Number", type: "tel" },
      { label: "Alternate Number", type: "tel" },
    ],
  },
  {
    title: "Advertising Details",
    fields: [
      { label: "Advertising Goal", type: "select", options: ["Brand Awareness", "Increase Sales", "App Visibility", "Product Promotion", "Lead Generation"] },
      { label: "Preferred Ad Type", type: "checkboxGroup", options: ["Featured Listing", "Banner Ad", "Sponsored Category", "Push Notification", "Festival Campaign", "Custom Plan"], wide: true },
      { label: "Target Audience", type: "textarea", wide: true },
      { label: "Preferred Start Date", type: "date" },
      { label: "Campaign Duration", type: "select", options: ["1 Week", "2 Weeks", "1 Month", "3 Months", "Custom"] },
      { label: "Estimated Budget", type: "text" },
    ],
  },
  {
    title: "File Uploads",
    fields: [
      { label: "Upload Company Logo", type: "file" },
      { label: "Upload Ad Creative / Sample Banner", type: "file" },
      { label: "Upload Product Images", type: "file" },
    ],
  },
  {
    title: "Additional Information",
    fields: [
      { label: "Special Requests or Notes", type: "textarea", wide: true },
      { label: "How Did You Hear About Us?", type: "select", options: ["Website", "Social Media", "Referral", "Other"] },
      { label: "I confirm that the information provided is accurate.", type: "checkbox", wide: true },
      { label: "I agree to RGHT NOW's advertising terms and policies", type: "checkbox", wide: true },
    ],
  },
];

const Field = ({ field }) => {
  const id = field.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  if (field.type === "textarea") {
    return (
      <label className={`rght-field ${field.wide ? "is-wide" : ""}`} htmlFor={id}>
        <span>{field.label}</span>
        <textarea id={id} placeholder={field.label} rows={4} />
      </label>
    );
  }

  if (field.type === "select") {
    return (
      <label className={`rght-field ${field.wide ? "is-wide" : ""}`} htmlFor={id}>
        <span>{field.label}</span>
        <select id={id} defaultValue="">
          <option value="" disabled>{field.label}</option>
          {field.options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === "checkboxGroup") {
    return (
      <div className="rght-field is-wide">
        <span>{field.label}</span>
        <div className="rght-check-grid">
          {field.options.map((option) => (
            <label className="rght-check-option" key={option}>
              <input type="checkbox" value={option} />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <label className="rght-check-option rght-field is-wide">
        <input type="checkbox" />
        <span>{field.label}</span>
      </label>
    );
  }

  if (field.type === "file") {
    return (
      <label className="rght-field rght-file-field" htmlFor={id}>
        <span>{field.label}</span>
        <input id={id} type="file" accept=".jpg,.jpeg,.gif,.png" />
        <strong>
          <UploadSimple size={18} /> Choose a file
        </strong>
      </label>
    );
  }

  return (
    <label className={`rght-field ${field.wide ? "is-wide" : ""}`} htmlFor={id}>
      <span>{field.label}</span>
      <input id={id} type={field.type} placeholder={field.label} />
    </label>
  );
};

const AdvertiseWithUsPage = () => (
  <div className="rght-page advertise-with-us-page">
    <RghtnowMarketingHeader active="advertise" />

    <main>
      <section className="rght-hero rght-hero-advertise">
        <div className="container rght-hero-grid">
          <div className="rght-hero-copy">
            <span className="rght-kicker">Advertise With Us</span>
            <h1>
              Put Your Business Where Customers Are - Grow with <em>RGHT NOW</em>
            </h1>
            <p>
              Promote your brand across groceries, restaurants, pharmacies, retail stores, and local services through
              Dubai's growing multi-service marketplace. Increase visibility, engagement, and sales with targeted
              advertising solutions.
            </p>
            <div className="rght-actions">
              <a className="rght-button rght-button-pink" href="#advertise-lead-form">
                Advertise Now <ArrowRight size={18} />
              </a>
              <a className="rght-button rght-button-line" href="https://rghtnow.com/contact-us/" target="_blank" rel="noreferrer">
                Talk to Our Team <PhoneCall size={17} weight="fill" />
              </a>
            </div>
          </div>
          <div className="rght-hero-art">
            <div className="rght-hero-shape" />
            <img src={upload("2026/06/advertise-1024x741.png")} alt="Advertise with RGHT NOW" />
          </div>
        </div>
      </section>

      <AnimatedStats subtitle="Give your campaign visibility inside a growing multi-service marketplace serving active local shoppers." />

      <section className="rght-section">
        <div className="container">
          <div className="rght-heading">
            <h2>
              Why Advertise With <em>RGHT NOW?</em>
            </h2>
            <p>Reach a highly engaged, purchase-ready audience across multiple service categories on a fast-growing platform.</p>
          </div>
          <div className="rght-card-grid">
            {whyAdvertise.map((item) => (
              <article className="rght-icon-card" key={item.title}>
                <img src={item.image} alt="" />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rght-section rght-soft">
        <div className="container">
          <div className="rght-heading">
            <h2>
              Advertising <em>Solutions</em>
            </h2>
            <p>Multiple formats designed to maximize your brand's visibility and drive real customer action.</p>
          </div>
          <div className="rght-card-grid">
            {advertisingSolutions.map((item) => (
              <article className="rght-icon-card" key={item.title}>
                <img src={item.image} alt="" />
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rght-section">
        <div className="container">
          <div className="rght-heading">
            <h2>
              Who Should <em>Advertise?</em>
            </h2>
            <p>Businesses across all industries can benefit from advertising on RGHT NOW's engaged marketplace audience.</p>
          </div>
          <div className="rght-audience-grid">
            {advertiserTypes.map((item) => {
              const Icon = item.icon;

              return (
                <article className="rght-audience-card" key={item.title}>
                  <span>
                    <Icon size={32} weight="fill" />
                  </span>
                  <h3>{item.title}</h3>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="rght-section rght-soft">
        <div className="container rght-split-grid">
          <div>
            <span className="rght-kicker">Performance</span>
            <h2>
              Campaign Performance <em>Benefits</em>
            </h2>
            <p>Every advertising campaign on RGHT NOW is designed to deliver tangible, measurable results for your business.</p>
            <ul className="rght-check-list">
              {performanceBenefits.map((benefit) => (
                <li key={benefit}>
                  <CheckCircle size={20} weight="fill" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          <img src={upload("2026/06/ChatGPT-Image-Jun-8-2026-11_29_32-AM-1024x817.png")} alt="Advertising performance benefits" />
        </div>
      </section>

      <section className="rght-section">
        <div className="container">
          <div className="rght-heading">
            <h2>
              Marketplace <em>Segments</em>
            </h2>
            <p>Advertising opportunities are available across our various service categories</p>
          </div>
          <div className="rght-picture-grid">
            {marketplaceSegments.map((segment) => (
              <article className="rght-picture-card" key={segment.title}>
                <img src={segment.image} alt="" />
                <h3>{segment.title}</h3>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rght-section rght-ready-section">
        <div className="container">
          <div className="rght-ready-panel">
            <img src={upload("2026/05/shop-rghtnow.svg")} alt="" />
            <div>
              <span className="rght-kicker">Advertise with RGHT NOW</span>
              <h2>Ready to Grow Your Brand?</h2>
              <p>Join hundreds of businesses already reaching thousands of active shoppers on RGHT NOW.</p>
            </div>
            <a className="rght-button rght-button-pink" href="#advertise-lead-form">
              Start Campaign <Megaphone size={18} weight="fill" />
            </a>
          </div>
        </div>
      </section>

      <section className="rght-section rght-form-section" id="advertise-lead-form">
        <div className="container rght-lead-grid">
          <div className="rght-lead-copy">
            <span className="rght-kicker">Lead Form</span>
            <h2>Start Your Advertising Journey</h2>
            <p>Share your business details and our team will help you create the perfect advertising campaign.</p>
            <div className="rght-lead-points">
              <span><Target size={22} weight="fill" /> Targeted category reach</span>
              <span><MapPin size={22} weight="fill" /> Dubai and UAE audience</span>
              <span><BellRinging size={22} weight="fill" /> App-first campaign formats</span>
              <span><CalendarBlank size={22} weight="fill" /> Flexible campaign duration</span>
            </div>
          </div>
          <form className="rght-form-card" onSubmit={(event) => event.preventDefault()}>
            {formSections.map((section) => (
              <fieldset className="rght-form-block" key={section.title}>
                <legend>{section.title}</legend>
                <div className="rght-field-grid">
                  {section.fields.map((field) => (
                    <Field field={field} key={field.label} />
                  ))}
                </div>
              </fieldset>
            ))}
            <button className="rght-button rght-button-pink rght-submit-button" type="submit">
              Submit Details <ArrowRight size={18} />
            </button>
          </form>
        </div>
      </section>

      <section className="rght-seller-strip">
        <div className="container rght-seller-inner">
          <div>
            <h2>Start Selling on RGHT NOW Today!</h2>
            <p>More orders. More customers. More growth.</p>
          </div>
          <div className="rght-store-badges">
            <img src={upload("2023/09/Google_Play_Store_badge_EN.svg-1024x303.png")} alt="Google Play" />
            <img src={upload("2023/09/Download_on_the_App_Store_Badge.svg-1024x304.webp")} alt="App Store" />
          </div>
          <ShoppingBagOpen size={50} weight="fill" />
        </div>
      </section>
    </main>

    <RghtnowMarketingFooter />
  </div>
);

export default AdvertiseWithUsPage;
